import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ProductCombobox from "@/components/ProductCombobox";
import {
  Plus, Trash, FileText, ArrowLeft, FloppyDisk, FilePdf,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const GST = 0.05;

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

export default function InvoiceBuilder() {
  const navigate = useNavigate();
  const { id: editId } = useParams();          // present when /invoices/:id/edit
  const isEditMode = !!editId;
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);

  // Customer
  const [invoiceType, setInvoiceType] = useState("tax"); // tax | proforma
  const [custType, setCustType] = useState("Individual");
  const [customer, setCustomer] = useState({
    organization_name: "", name: "", address: "", phone: "", email: "", gstin: "",
  });
  // For Organization: when true (default), individual contact uses org address
  const [sameAsOrg, setSameAsOrg] = useState(true);
  const [orgAddress, setOrgAddress] = useState("");
  const [sameDelivery, setSameDelivery] = useState(true);
  const [delivery, setDelivery] = useState({ name: "", address: "", phone: "" });

  // Items (each row carries a stable _key for React reconciliation)
  const [items, setItems] = useState(() => [
    { _key: `row-${Date.now()}-0`, product_id: "", description: "", hsn: "21069099", qty: 1, rate: 0 },
  ]);

  // Options
  const [zeroInvoice, setZeroInvoice] = useState(false);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [includeShipping, setIncludeShipping] = useState(false);
  const [discountPercent, setDiscountPercent] = useState("");
  const [shippingAmount, setShippingAmount] = useState("");
  const [showUpiQr, setShowUpiQr] = useState(true);
  const [paymentMode, setPaymentMode] = useState("Immediate");
  const [paymentTerms, setPaymentTerms] = useState("NEFT/RTGS/IMPS");

  // Payment received (marks invoice as paid + records txn details)
  const [paid, setPaid] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Invoice number (auto-suggested, user-editable)
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceNumberSuggested, setInvoiceNumberSuggested] = useState("");

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await api.get("/products", { params: { is_active: true } });
      setProducts(data);
    } catch (err) {
      console.error("Failed to load products:", err);
    }
  }, []);

  const loadNextNumber = useCallback(async () => {
    try {
      const { data } = await api.get("/invoices/next-number");
      setInvoiceNumberSuggested(data.next_number || "");
      setInvoiceNumber(data.next_number || "");
    } catch (err) {
      console.error("Failed to load next invoice number:", err);
    }
  }, []);

  // eslint-disable-next-line
  useEffect(() => { loadProducts(); }, [loadProducts]);

  /**
   * Apply an invoice-like object to all form state. Used by both
   * Duplicate (from sessionStorage) and Edit (from API fetch).
   */
  const applyPrefill = useCallback((source, opts = {}) => {
    const { isEdit = false } = opts;
    if (!source) return;
    const c = source.customer || {};
    const isOrg = (c.type || "Individual") === "Organization";
    setInvoiceType(source.invoice_type || "tax");
    setCustType(c.type || "Individual");
    setCustomer({
      organization_name: c.organization_name || "",
      name: c.name || "",
      address: c.address || "",
      phone: c.phone || "",
      email: c.email || "",
      gstin: c.gstin || "",
    });
    setOrgAddress(isOrg ? (c.address || "") : "");
    setSameAsOrg(isOrg);  // user can toggle off; same as create defaults
    const d = source.delivery_address;
    setSameDelivery(!d);
    if (d) {
      setDelivery({
        name: d.name || "",
        address: d.address || "",
        phone: d.phone || "",
      });
    }
    const srcItems = Array.isArray(source.items) ? source.items : [];
    setItems(srcItems.length > 0
      ? srcItems.map((it, idx) => ({
          _key: `row-${Date.now()}-${idx}`,
          product_id: it.product_id || "",
          description: it.description || "",
          hsn: it.hsn || "21069099",
          qty: it.qty ?? 1,
          rate: it.rate ?? 0,
        }))
      : [{ _key: `row-${Date.now()}-0`, product_id: "", description: "", hsn: "21069099", qty: 1, rate: 0 }]
    );
    const opt = source.options || {};
    setZeroInvoice(!!opt.zero_invoice);
    setApplyDiscount(!!opt.apply_discount);
    setIncludeShipping(!!opt.include_shipping);
    setDiscountPercent(source.discount_percent ? String(source.discount_percent) : "");
    setShippingAmount(source.shipping_amount ? String(source.shipping_amount) : "");
    setShowUpiQr(source.show_upi_qr ?? true);
    setPaymentMode(source.payment_mode || "Immediate");
    setPaymentTerms(source.payment_terms || "NEFT/RTGS/IMPS");
    if (isEdit) {
      setInvoiceNumber(source.invoice_number || "");
      setInvoiceNumberSuggested(source.invoice_number || "");
      // Paid status fields stay locked in edit mode (paid edits are blocked
      // by the backend) so we ignore them here.
    }
  }, []);

  /**
   * Edit mode: load the invoice from the API and prefill.
   * Duplicate mode: read the prefill payload from sessionStorage and clear it.
   * New mode: fetch the next-number suggestion.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isEditMode) {

        setPrefillLoading(true);
        try {
          const { data } = await api.get(`/invoices/${editId}`);
          if (cancelled) return;
          applyPrefill(data, { isEdit: true });
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Failed to load invoice for edit");
          navigate("/invoices");
        } finally {
          if (!cancelled) setPrefillLoading(false);
        }
        return;
      }
      // Not edit — get next number first, then check for duplicate prefill
      await loadNextNumber();
      try {
        const raw = sessionStorage.getItem("invoice-duplicate");
        if (raw && !cancelled) {
          const parsed = JSON.parse(raw);
          applyPrefill(parsed, { isEdit: false });
          // Clear ONLY after successful prefill so React 18 StrictMode's
          // double-mount in dev (and any retried mount) can still consume it.
          sessionStorage.removeItem("invoice-duplicate");
          toast.success("Duplicated invoice — review and generate when ready");
        }
      } catch {
        // ignore broken sessionStorage payload
        sessionStorage.removeItem("invoice-duplicate");
      }
    })();
    return () => { cancelled = true; };
  }, [isEditMode, editId, loadNextNumber, applyPrefill, navigate]);

  // Live totals
  const totals = useMemo(() => {
    const subTotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
    const mrpTaxable = subTotal / (1 + GST);
    const d = zeroInvoice ? 1 : (applyDiscount ? Math.max(0, Math.min(100, Number(discountPercent || 0))) / 100 : 0);
    const discountAmount = mrpTaxable * d;
    const taxable = mrpTaxable - discountAmount;
    const sgst = taxable * (GST / 2);
    const cgst = taxable * (GST / 2);
    const shipping = includeShipping ? Number(shippingAmount || 0) : 0;
    const total = taxable + sgst + cgst + shipping;
    return {
      subTotal, mrpTaxable, discountAmount,
      discountPct: d * 100,
      taxable, sgst, cgst, shipping, total,
    };
  }, [items, zeroInvoice, applyDiscount, discountPercent, includeShipping, shippingAmount]);

  const addItem = () => setItems((arr) => [...arr, { product_id: "", description: "", hsn: "21069099", qty: 1, rate: 0 }]);
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const pickProduct = (i, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    setItems((arr) => arr.map((it, idx) => idx === i ? {
      ...it,
      product_id: p.id,
      description: p.name,
      hsn: p.hsn,
      rate: p.mrp,
      qty: it.qty || 1,
    } : it));
  };

  const validate = () => {
    if (!customer.name.trim()) { toast.error("Customer name is required"); return false; }
    if (custType === "Organization" && !customer.gstin.trim()) {
      toast.error("GSTIN is required for organizations"); return false;
    }
    const cleanItems = items.filter((it) => it.description?.trim() && Number(it.qty) > 0);
    if (cleanItems.length === 0) { toast.error("Add at least one item"); return false; }
    return cleanItems;
  };

  const submit = async (status) => {
    const clean = validate();
    if (!clean) return;
    setSaving(true);
    try {
      // Resolve customer block (org logic + same-as-org address)
      const isOrg = custType === "Organization";
      const effectiveAddress = isOrg && sameAsOrg ? orgAddress : customer.address;
      const customerOut = {
        type: custType,
        organization_name: isOrg ? (customer.organization_name || "") : "",
        name: customer.name,
        address: effectiveAddress,
        phone: customer.phone,
        email: customer.email,
        gstin: customer.gstin || "",
      };
      const deliveryOut = sameDelivery
        ? null
        : {
            type: custType,
            organization_name: isOrg ? customerOut.organization_name : "",
            name: delivery.name || customer.name,
            address: delivery.address,
            phone: delivery.phone,
          };
      const payload = {
        status,
        invoice_number: invoiceNumber || undefined,
        invoice_type: invoiceType,
        customer: customerOut,
        delivery_address: deliveryOut,
        items: clean.map((it) => ({
          product_id: it.product_id || "",
          description: it.description,
          hsn: it.hsn || "21069099",
          qty: Number(it.qty),
          rate: Number(it.rate),
        })),
        options: { zero_invoice: zeroInvoice, apply_discount: applyDiscount, include_shipping: includeShipping },
        discount_percent: Number(discountPercent || 0),
        shipping_amount: Number(shippingAmount || 0),
        show_upi_qr: showUpiQr,
        payment_mode: paymentMode,
        payment_terms: paymentTerms,
        paid: paid,
        transaction_id: paid ? transactionId : "",
        payment_date: paid ? paymentDate : "",
      };
      const { data } = isEditMode
        ? await api.put(`/invoices/${editId}`, payload)
        : await api.post("/invoices", payload);
      toast.success(
        isEditMode
          ? `Invoice updated: ${data.invoice_number}`
          : `${status === "draft" ? "Draft saved" : "Invoice generated"}: ${data.invoice_number}`
      );
      navigate(`/invoices/${data.id}`);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Failed to create invoice";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="invoice-builder">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/invoices")} className="p-2 rounded-md hover:bg-slate-100 text-slate-600" data-testid="back-to-invoices"><ArrowLeft size={18} /></button>
          <div className="flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Billing</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {isEditMode ? "Edit Invoice" : "New Invoice"}
              {prefillLoading && <span className="ml-3 text-xs font-normal text-slate-400">loading…</span>}
            </h1>
          </div>
          {!isEditMode && (
            <button onClick={() => submit("draft")} disabled={saving} className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-60" data-testid="save-draft-button">
              <FloppyDisk size={16} /> Save Draft
            </button>
          )}
          <button onClick={() => submit("issued")} disabled={saving || prefillLoading} className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60" data-testid="generate-invoice-button">
            <FilePdf size={16} weight="bold" />
            {saving
              ? (isEditMode ? "Saving…" : "Generating…")
              : (isEditMode ? "Save Changes" : "Generate Invoice")}
          </button>
        </div>
      </div>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice type */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Invoice Type</div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setInvoiceType("tax")}
                className={
                  "px-4 h-10 rounded-md border text-sm font-medium transition " +
                  (invoiceType === "tax"
                    ? "border-blue-600 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")
                }
                data-testid="invoice-type-tax"
              >Tax Invoice</button>
              <button
                type="button"
                onClick={() => setInvoiceType("proforma")}
                className={
                  "px-4 h-10 rounded-md border text-sm font-medium transition " +
                  (invoiceType === "proforma"
                    ? "border-blue-600 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")
                }
                data-testid="invoice-type-proforma"
              >Proforma Invoice</button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {invoiceType === "tax"
                ? "Standard GST invoice. Treated as a final demand for payment."
                : "Preliminary bill of sale before goods or services are delivered. Not used for accounting."}
            </p>

            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Invoice Number</Label>
                {invoiceNumberSuggested && invoiceNumber !== invoiceNumberSuggested && (
                  <button
                    type="button"
                    onClick={() => setInvoiceNumber(invoiceNumberSuggested)}
                    className="text-[11px] text-blue-600 hover:text-blue-700 font-medium"
                    data-testid="invoice-number-reset"
                  >
                    Reset to suggested ({invoiceNumberSuggested})
                  </button>
                )}
              </div>
              <Input
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder={invoiceNumberSuggested || "e.g. SVRAF/26-27/004"}
                className="font-mono"
                data-testid="invoice-number-input"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                Auto-suggested. Edit only when you need to override the sequence (e.g. cancelled invoice gap).
                Must be unique — duplicate numbers are rejected.
              </p>
            </div>
          </div>

          {/* Customer */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Customer</div>
              <Select value={custType} onValueChange={setCustType}>
                <SelectTrigger className="w-40 h-9" data-testid="customer-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Individual">Individual</SelectItem>
                  <SelectItem value="Organization">Organization</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {custType === "Organization" && (
                <>
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Organization Name</Label>
                    <Input
                      value={customer.organization_name}
                      onChange={(e) => setCustomer({ ...customer, organization_name: e.target.value })}
                      className="mt-1.5"
                      placeholder="e.g. Acme Foods Pvt Ltd"
                      data-testid="invc-orgname"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Organization Address</Label>
                    <Textarea rows={2} value={orgAddress} onChange={(e) => setOrgAddress(e.target.value)} className="mt-1.5" data-testid="invc-orgaddress" />
                  </div>
                </>
              )}
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  {custType === "Organization" ? "Contact Person Name" : "Name"}
                </Label>
                <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="mt-1.5" data-testid="invc-name" />
              </div>
              {custType === "Organization" && (
                <label className="col-span-2 flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={sameAsOrg} onCheckedChange={(v) => setSameAsOrg(!!v)} data-testid="opt-sameasorg" />
                  <span className="text-sm text-slate-700">Contact person address same as organization address</span>
                </label>
              )}
              {!(custType === "Organization" && sameAsOrg) && (
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    {custType === "Organization" ? "Contact Person Address" : "Address"}
                  </Label>
                  <Textarea rows={2} value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} className="mt-1.5" />
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Phone</Label>
                <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
                <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="mt-1.5" />
              </div>
              {custType === "Organization" && (
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">GSTIN</Label>
                  <Input value={customer.gstin} onChange={(e) => setCustomer({ ...customer, gstin: e.target.value.toUpperCase() })} className="mt-1.5" data-testid="invc-gstin" />
                </div>
              )}
              <label className="col-span-2 flex items-center gap-2 cursor-pointer">
                <Checkbox checked={sameDelivery} onCheckedChange={(v) => setSameDelivery(!!v)} />
                <span className="text-sm text-slate-700">Delivery address same as buyer</span>
              </label>
              {!sameDelivery && (
                <>
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Delivery Name</Label>
                    <Input value={delivery.name} onChange={(e) => setDelivery({ ...delivery, name: e.target.value })} className="mt-1.5" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Delivery Address</Label>
                    <Textarea rows={2} value={delivery.address} onChange={(e) => setDelivery({ ...delivery, address: e.target.value })} className="mt-1.5" />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Items</div>
              <button onClick={addItem} className="inline-flex items-center gap-1 px-2 h-8 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700" data-testid="add-item-button"><Plus size={12} weight="bold" /> Add Row</button>
            </div>
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={it._key || `idx-${i}`} className="grid grid-cols-12 gap-2 items-end" data-testid={`item-row-${i}`}>
                  <div className="col-span-4">
                    {i === 0 && <Label className="text-[10px] uppercase tracking-wider text-slate-500">Product</Label>}
                    <ProductCombobox
                      products={products}
                      value={it.product_id}
                      onPick={(v) => pickProduct(i, v)}
                      testId={`item-product-${i}`}
                    />
                  </div>
                  <div className="col-span-4">
                    {i === 0 && <Label className="text-[10px] uppercase tracking-wider text-slate-500">Description</Label>}
                    <Input className="h-9 mt-1" value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} placeholder="Description" data-testid={`item-desc-${i}`} />
                  </div>
                  <div className="col-span-1">
                    {i === 0 && <Label className="text-[10px] uppercase tracking-wider text-slate-500">Qty</Label>}
                    <Input className="h-9 mt-1" type="number" min="0" value={it.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} data-testid={`item-qty-${i}`} />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-[10px] uppercase tracking-wider text-slate-500">Rate (₹)</Label>}
                    <Input className="h-9 mt-1" type="number" min="0" value={it.rate} onChange={(e) => updateItem(i, "rate", e.target.value)} data-testid={`item-rate-${i}`} />
                  </div>
                  <div className="col-span-1">
                    <button onClick={() => removeItem(i)} className="h-9 mt-1 w-full rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center" data-testid={`item-remove-${i}`}><Trash size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Options</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={zeroInvoice} onCheckedChange={(v) => setZeroInvoice(!!v)} data-testid="opt-zero" />
                <span className="text-sm text-slate-700">Zero Invoice (100% discount)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={applyDiscount} onCheckedChange={(v) => setApplyDiscount(!!v)} disabled={zeroInvoice} data-testid="opt-discount" />
                <span className="text-sm text-slate-700">Apply Discount</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={includeShipping} onCheckedChange={(v) => setIncludeShipping(!!v)} data-testid="opt-shipping" />
                <span className="text-sm text-slate-700">Include Shipping</span>
              </label>
              {applyDiscount && !zeroInvoice && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Discount %</Label>
                  <Input type="number" min="0" max="100" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} className="mt-1.5" data-testid="discount-percent" />
                </div>
              )}
              {includeShipping && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Shipping (₹)</Label>
                  <Input type="number" min="0" value={shippingAmount} onChange={(e) => setShippingAmount(e.target.value)} className="mt-1.5" data-testid="shipping-amount" />
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer col-span-3 pt-2 border-t border-slate-100">
                <Checkbox checked={showUpiQr} onCheckedChange={(v) => setShowUpiQr(!!v)} data-testid="opt-upi" />
                <span className="text-sm text-slate-700">Show UPI QR Code on invoice</span>
              </label>

              <div className="col-span-3 pt-3 border-t border-slate-100">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Payment Terms</Label>
                <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                  <SelectTrigger className="mt-1.5 h-9 w-64" data-testid="payment-terms-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NEFT/RTGS/IMPS">NEFT/RTGS/IMPS</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-3 pt-3 border-t border-slate-100 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={paid} onCheckedChange={(v) => setPaid(!!v)} data-testid="opt-paid" />
                  <span className="text-sm text-slate-700">Mark as paid (record transaction details)</span>
                </label>
                {paid && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6 border-l-2 border-emerald-200 ml-1 py-1" data-testid="paid-details">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500">Transaction ID</Label>
                      <Input
                        value={transactionId}
                        onChange={(e) => setTransactionId(e.target.value)}
                        className="mt-1.5"
                        placeholder="e.g. TXN-ABC-123456 / UTR / Cheque #"
                        data-testid="paid-txn-id"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500">Payment Date</Label>
                      <Input
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        className="mt-1.5"
                        data-testid="paid-date"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-lg p-6 sticky top-24" data-testid="live-totals">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4">Live Totals</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Sub Total (incl. GST)</span><span className="font-medium">₹ {fmt(totals.subTotal)}</span></div>
              <div className="flex justify-between text-xs"><span className="text-slate-400">Pre-tax taxable</span><span className="text-slate-500">₹ {fmt(totals.mrpTaxable)}</span></div>
              {totals.discountAmount > 0 && (
                <div className="flex justify-between text-rose-600"><span>Discount ({totals.discountPct.toFixed(0)}%)</span><span>- ₹ {fmt(totals.discountAmount)}</span></div>
              )}
              <div className="flex justify-between border-t border-slate-100 pt-2 font-medium"><span>Taxable Value</span><span>₹ {fmt(totals.taxable)}</span></div>
              <div className="flex justify-between text-slate-600"><span>SGST @ 2.5%</span><span>₹ {fmt(totals.sgst)}</span></div>
              <div className="flex justify-between text-slate-600"><span>CGST @ 2.5%</span><span>₹ {fmt(totals.cgst)}</span></div>
              {totals.shipping > 0 && (
                <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span>₹ {fmt(totals.shipping)}</span></div>
              )}
              <div className="flex justify-between border-t-2 border-slate-200 pt-3 mt-3 text-lg font-bold text-slate-900"><span>Total</span><span data-testid="totals-grand">₹ {fmt(totals.total)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
